import React, {
	type ReactElement,
	useEffect,
	useMemo,
	useRef,
	useState,
	useCallback,
	type RefCallback,
} from 'react';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import type { DropdownTriggerParams } from '@atlassian/jira-development-summary-common/src/common/types.tsx';
import { useIssueId, useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIssueTypeField } from '@atlassian/jira-issue-field-issue-type/src/services/index.tsx';
import { useSummaryField } from '@atlassian/jira-issue-field-summary-common/src/services/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type { CreateBranchTarget } from '../../common/types.tsx';
import { useBranchCommandBuilderStore } from '../../controllers/branch-command-builder-store/index.tsx';
import { createBranchDropdownContentEntrypoint } from './create-branch-dropdown-content/entrypoint.tsx';

export type Props = {
	getDropdownTrigger: (props: DropdownTriggerParams) => ReactElement;
	createBranchTargets: CreateBranchTarget[];
	hideCreateBranchLinks?: boolean;
	onOpenDropdown?: (arg1: { isOpen: boolean }) => void;
};

export const useIsDropdownOpen = (isOpen = false) => useState(isOpen);

const entryPointParams = {};
const CreateBranchDropdownInner = ({
	createBranchTargets,
	onOpenDropdown,
	getDropdownTrigger,
	hideCreateBranchLinks = false,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useIsDropdownOpen();
	const [isBranchConfigOpen, setIsBranchConfigOpen] = useState(false);
	const previousOpen = useRef(false);
	const [, { getUserConfigBranchData, setBranchData }] = useBranchCommandBuilderStore();
	const configButtonRef = useRef<HTMLButtonElement | null>(null);
	const accountId = useAccountId();
	const issueKey = useIssueKey();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const issueId = fg('relay-migration-issue-fields-summary') ? useIssueId() : undefined;
	const [{ value: issueType }] = useIssueTypeField({ issueKey });
	const [{ value: issueSummary }] = useSummaryField({ issueKey, issueId });
	const projectKey = useProjectKey(issueKey);
	const menuGroupRef = useRef<HTMLDivElement>(null);
	const [activeItemId, setActiveItemId] = useState<string | null>(null);

	const handleOnClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'updateBranchNameFormatButton',
		});

		fireUIAnalytics(analyticsEvent);

		setIsBranchConfigOpen(true);
	}, [createAnalyticsEvent]);

	const handleOnHover = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'hovered',
			actionSubject: 'updateBranchNameFormatButton',
		});

		fireUIAnalytics(analyticsEvent);
	}, [createAnalyticsEvent]);

	useEffect(() => {
		if (previousOpen.current !== isOpen) {
			previousOpen.current = isOpen;
			if (isOpen) {
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown opened', 'createBranchDropdown');
				setBranchData({
					issueKey,
					issueSummary: issueSummary || '',
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					issueType: (issueType?.name as string) || '',
					projectKey,
				});
				getUserConfigBranchData(accountId);

				setTimeout(() => {
					if (!isBranchConfigOpen && menuGroupRef.current && fg('dev_details_add_focus_to_list')) {
						const firstMenuItem =
							menuGroupRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
						if (firstMenuItem) {
							firstMenuItem.focus();
							setActiveItemId(firstMenuItem.getAttribute('data-testid'));
						}
					}
				}, 0);
			} else {
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown closed', 'createBranchDropdown');
				isBranchConfigOpen && setIsBranchConfigOpen(false);
			}
			onOpenDropdown && onOpenDropdown({ isOpen });
		}
	}, [
		createAnalyticsEvent,
		createBranchTargets,
		isOpen,
		onOpenDropdown,
		accountId,
		getUserConfigBranchData,
		setBranchData,
		issueKey,
		issueSummary,
		issueType?.name,
		projectKey,
		isBranchConfigOpen,
	]);

	const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
		if (!fg('dev_details_add_focus_to_list')) return;
		if (!menuGroupRef.current) return;

		const menuItems: HTMLElement[] = Array.from(
			menuGroupRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]'),
		);
		if (menuItems.length === 0) return;

		const currentIndex = menuItems.findIndex((item) => item === globalThis.document?.activeElement);

		switch (event.key) {
			case 'ArrowDown': {
				event.preventDefault();
				const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
				menuItems[nextIndex].focus();
				// Update active item tracking using data-testid
				setActiveItemId(menuItems[nextIndex].getAttribute('data-testid') || null);
				break;
			}
			case 'ArrowUp': {
				event.preventDefault();
				const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
				menuItems[prevIndex].focus();
				// Update active item tracking using data-testid
				setActiveItemId(menuItems[prevIndex].getAttribute('data-testid') || null);
				break;
			}
			default: {
				break;
			}
		}
	}, []);

	useEffect(() => {
		if (!isBranchConfigOpen && configButtonRef.current) {
			configButtonRef.current.focus();
		}
	}, [isBranchConfigOpen]);

	const showCreateBranchLinkSection = !hideCreateBranchLinks && createBranchTargets.length > 0;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		createBranchDropdownContentEntrypoint,
		entryPointParams,
	);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);
	const runtimeProps = useMemo(
		() => ({
			createBranchTargets,
			isBranchConfigOpen,
			setIsBranchConfigOpen,
			showCreateBranchLinkSection,
			menuGroupRef,
			activeItemId,
			handleKeyDown,
			setIsOpen,
			configButtonRef,
			handleOnClick,
			handleOnHover,
		}),
		[
			activeItemId,
			createBranchTargets,
			handleKeyDown,
			handleOnClick,
			handleOnHover,
			isBranchConfigOpen,
			setIsOpen,
			showCreateBranchLinkSection,
		],
	);

	return (
		<Popup
			id="create-branch-dropdown"
			zIndex={layers.modal + 1}
			placement="bottom-end"
			shouldFlip
			messageId="development-summary-branch.ui.create-branch-dropdown.popup"
			messageType="transactional"
			trigger={(triggerProps) =>
				getDropdownTrigger({
					...triggerProps,
					ref: (element) => {
						triggerRef(element);
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(triggerProps.ref as RefCallback<HTMLElement>)(element);
					},
					isSelected: isOpen,
					onClick: () => setIsOpen(!isOpen),
				})
			}
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
				setIsBranchConfigOpen(false);
			}}
			content={() => (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="dev-panel-create-dropdown-content"
					packageName="jira-development-summary-branch"
					fallback={null}
					runtimeProps={runtimeProps}
				/>
			)}
		/>
	);
};

export const CreateBranchDropdown = ({
	createBranchTargets,
	onOpenDropdown,
	getDropdownTrigger,
	hideCreateBranchLinks = false,
}: Props) => {
	const scmProviderTypes = useMemo(
		() => createBranchTargets.map((target) => target.applicationType),
		[createBranchTargets],
	);

	return (
		<ContextualAnalyticsData
			attributes={{
				scmProviderTypes,
				singleScmProvider: createBranchTargets.length === 1,
			}}
		>
			<CreateBranchDropdownInner
				createBranchTargets={createBranchTargets}
				onOpenDropdown={onOpenDropdown}
				getDropdownTrigger={getDropdownTrigger}
				hideCreateBranchLinks={hideCreateBranchLinks}
			/>
		</ContextualAnalyticsData>
	);
};

export default CreateBranchDropdown;
